/** @file Value axis of the curve graph: graph units <-> y pixels, zoom at the pointer, pan, fit. */
import { clamp } from '@pose2d/math/clamp';
import type { CurveValueScale } from './curveTypes';

const MIN_PX_PER_UNIT = 1e-4;
const MAX_PX_PER_UNIT = 1e6;

/** Value-axis math; y is canvas pixels from the top edge and graph units grow upward. */
export const curveValueScale = {
  /** Smallest pixels-per-unit any operation produces. */
  MIN_PX_PER_UNIT,
  /** Largest pixels-per-unit any operation produces. */
  MAX_PX_PER_UNIT,
  /** y pixel of graph unit g. */
  toY(s: CurveValueScale, g: number): number {
    return (s.top - g) * s.pxPerUnit;
  },
  /** Graph unit at y pixels. */
  toValue(s: CurveValueScale, y: number): number {
    return s.top - y / s.pxPerUnit;
  },
  /** Multiplies pixels-per-unit by factor (clamped), keeping the graph unit under y in place. */
  zoomAt(s: CurveValueScale, y: number, factor: number): CurveValueScale {
    const pxPerUnit = clamp(s.pxPerUnit * factor, MIN_PX_PER_UNIT, MAX_PX_PER_UNIT);
    return { top: s.top - y / s.pxPerUnit + y / pxPerUnit, pxPerUnit };
  },
  /** Moves the content down by dy pixels, as when the graph is dragged. */
  pan(s: CurveValueScale, dy: number): CurveValueScale {
    return { top: s.top + dy / s.pxPerUnit, pxPerUnit: s.pxPerUnit };
  },
  /** Shows units lo..hi between y = top and y = bottom, padded by pad pixels; a flat range opens to ±1. */
  fit(lo: number, hi: number, top: number, bottom: number, pad: number): CurveValueScale {
    const [a, b] = hi - lo > 1e-9 ? [lo, hi] : [lo - 1, hi + 1];
    const pxPerUnit = clamp(Math.max(bottom - top - 2 * pad, 1) / (b - a), MIN_PX_PER_UNIT, MAX_PX_PER_UNIT);
    return { top: b + (top + pad) / pxPerUnit, pxPerUnit };
  },
};
